import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface ChinaPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  created_at: string;
  content_type?: string;
  tags?: { id: string; name: string }[];
}

interface ChinaItinerary {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  featured_image: string | null;
  destination: string | null;
  duration: number | null;
  best_season: string | null;
}

interface UseChinaContentOptions {
  contentType?: 'planning' | 'city' | 'resource' | 'itinerary' | 'country';
  limit?: number;
}

export function useChinaContent(options: UseChinaContentOptions = {}) {
  const { contentType, limit = 10 } = options;

  // Fetch China-tagged blog posts
  const postsQuery = useQuery({
    queryKey: ['china-posts', contentType, limit],
    queryFn: async (): Promise<ChinaPost[]> => {
      // First get the China tag IDs based on content type
      let tagQuery = supabase.from('tags').select('id');
      
      if (contentType === 'planning') {
        tagQuery = tagQuery.eq('name', 'China Planning');
      } else if (contentType === 'city') {
        tagQuery = tagQuery.eq('name', 'China Cities');
      } else if (contentType === 'resource') {
        tagQuery = tagQuery.eq('name', 'China Resources');
      } else {
        // Get all China-related tags
        tagQuery = tagQuery.or('name.eq.China,name.eq.China Planning,name.eq.China Cities,name.eq.China Resources');
      }

      const { data: tags, error: tagsError } = await tagQuery;
      
      if (tagsError || !tags || tags.length === 0) {
        return [];
      }

      const tagIds = tags.map(t => t.id);

      // Get blog posts with these tags
      const { data: tagLinks, error: linkError } = await supabase
        .from('blog_posts_tags')
        .select('blog_post_id')
        .in('tag_id', tagIds);

      if (linkError || !tagLinks || tagLinks.length === 0) {
        return [];
      }

      const postIds = [...new Set(tagLinks.map(l => l.blog_post_id))];

      // Fetch the actual posts
      const { data: posts, error: postsError } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, featured_image, created_at')
        .in('id', postIds)
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (postsError) {
        console.error('Error fetching China posts:', postsError);
        return [];
      }

      // Fetch tags for each post
      const postsWithTags = await Promise.all(
        (posts || []).map(async (post) => {
          const { data: postTags } = await supabase
            .from('blog_posts_tags')
            .select('tags:tag_id(id, name)')
            .eq('blog_post_id', post.id);

          return {
            ...post,
            tags: postTags?.map(pt => pt.tags as unknown as { id: string; name: string }) || []
          };
        })
      );

      return postsWithTags;
    },
    enabled: contentType !== 'itinerary',
  });

  // Fetch China itineraries
  const itinerariesQuery = useQuery({
    queryKey: ['china-itineraries', limit],
    queryFn: async (): Promise<ChinaItinerary[]> => {
      // Get China tag ID
      const { data: chinaTag } = await supabase
        .from('tags')
        .select('id')
        .eq('name', 'China')
        .single();

      if (!chinaTag) {
        return [];
      }

      // Get itineraries with China tag
      const { data: tagLinks } = await supabase
        .from('itinerary_tags')
        .select('itinerary_id')
        .eq('tag_id', chinaTag.id);

      if (!tagLinks || tagLinks.length === 0) {
        return [];
      }

      const itineraryIds = tagLinks.map(l => l.itinerary_id);

      const { data: itineraries, error } = await supabase
        .from('itineraries')
        .select('id, title, slug, description, featured_image, destination, duration, best_season')
        .in('id', itineraryIds)
        .eq('status', 'published')
        .order('title')
        .limit(limit);

      if (error) {
        console.error('Error fetching China itineraries:', error);
        return [];
      }

      return itineraries || [];
    },
    enabled: contentType === 'itinerary' || contentType === undefined,
  });

  return {
    posts: postsQuery.data || [],
    itineraries: itinerariesQuery.data || [],
    loading: postsQuery.isLoading || itinerariesQuery.isLoading,
    error: postsQuery.error || itinerariesQuery.error,
  };
}
